import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import { Toaster } from "@/components/ui/sonner"
import FlashMessages from "@/components/flash-messages"
import { track } from "@/lib/analytics"
import type { User, Account } from "@/types"
import ProgressIndicator from "@/components/onboarding/progress-indicator"
import WelcomeStep from "@/components/onboarding/welcome-step"
import AccountTypeStep from "@/components/onboarding/account-type-step"
import BrandSetupStep from "@/components/onboarding/brand-setup-step"
import ListingTeaserStep from "@/components/onboarding/listing-teaser-step"
import ReadyStep from "@/components/onboarding/ready-step"

const STEPS = ["welcome", "account_type", "brand_setup", "listing_teaser", "ready"] as const
type Step = (typeof STEPS)[number]

export default function OnboardingIndex({
  user,
  account,
  step: initialStep,
}: {
  user: User
  account: Account
  step: string
}) {
  const [step, setStep] = useState<Step>(
    STEPS.includes(initialStep as Step) ? (initialStep as Step) : "welcome"
  )

  function patchStep(stepName: string, data: Record<string, string> = {}) {
    router.patch("/onboarding", { step: stepName, ...data }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        if (stepName === "complete") {
          track("onboarding_completed")
        } else {
          track("onboarding_step_completed", { step: stepName })
        }
        const nextIndex = STEPS.indexOf(stepName as Step) + 1
        if (nextIndex > 0 && nextIndex < STEPS.length) {
          setStep(STEPS[nextIndex])
        }
      },
    })
  }

  function goToStep(stepName: Step) {
    setStep(stepName)
  }

  return (
    <>
      <Head title="Get Started | QuiteRight" />
      <FlashMessages />
      <Toaster position="top-right" richColors />
      <ProgressIndicator currentStep={step} />

      <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-20">
        <div className="relative w-full max-w-2xl">
          {step === "welcome" && (
            <WelcomeStep
              userName={user.name}
              onNext={() => goToStep("account_type")}
            />
          )}

          {step === "account_type" && (
            <AccountTypeStep
              currentType={account.account_type}
              onNext={(type) => patchStep("account_type", { account_type: type })}
            />
          )}

          {step === "brand_setup" && (
            <BrandSetupStep
              accountType={account.account_type}
              initialData={account}
              onNext={(data) => patchStep("brand_setup", data)}
            />
          )}

          {step === "listing_teaser" && (
            <ListingTeaserStep
              onNext={(url) => patchStep("listing_teaser", { source_url: url })}
              onSkip={() => patchStep("listing_teaser")}
            />
          )}

          {step === "ready" && (
            <ReadyStep
              onComplete={() => patchStep("complete")}
            />
          )}
        </div>
      </div>
    </>
  )
}
